import { useQuery } from '@tanstack/react-query';

import {
  CSD_SEAL_QUERY_KEY,
  getCsdSealService,
  type CsdSeal,
  type CsdSealActivation,
} from '../../services/csdSeal.service';

/**
 * Única fuente de verdad de «¿el usuario puede timbrar?» para el flujo nuevo.
 *
 * Contrato: la custodia del sello del fiscal-engine (`GET /csd/seal`) y nada
 * más. Es la misma fila con la que el Go firma el timbrado y la que lee su
 * `/operations/user`, así que no hay otro lado que consultar: un sello subido
 * por el flujo legacy también vive ahí.
 *
 * «No tiene sello» es una respuesta (404, o un sello vencido) y «no pudimos
 * revisar» es otra cosa (5xx o red): tratar la segunda como la primera manda a
 * subir otro sello a quien ya tiene uno vigente.
 */
export interface SealStatus {
  /** El sello existe y está vigente. Lo único que habilita timbrar. */
  hasSeal: boolean;
  /** Existe pero venció: es un estado distinto a «no tienes sello». */
  isExpired: boolean;
  seal: CsdSeal | null;
  isLoading: boolean;
  /** No sabemos si hay sello: la consulta falló sin respuesta. Nunca es «sin sello». */
  checkFailed: boolean;
  isRechecking: boolean;
  recheck: () => void;
  /** El SAT todavía no acepta el sello: `null` si ya lo acepta o no se sabe. */
  pendingActivation: CsdSealActivation | null;
}

export const useSealStatus = (): SealStatus => {
  const sealQuery = useQuery({
    queryKey: CSD_SEAL_QUERY_KEY,
    queryFn: getCsdSealService,
    retry: false,
  });

  const seal = sealQuery.data ?? null;

  return {
    hasSeal: !!seal?.is_valid,
    isExpired: !!seal && !seal.is_valid,
    seal,
    isLoading: sealQuery.isLoading,
    checkFailed: sealQuery.isError && sealQuery.data === undefined,
    isRechecking: sealQuery.isFetching,
    recheck: () => void sealQuery.refetch(),
    pendingActivation: seal?.is_valid && seal.activation?.status === 'pending' ? seal.activation : null,
  };
};
